'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface PricingOption {
  name: string;
  price: string;
  duration: string;
  features: string[];
  popular?: boolean;
}

interface PricingEnrollmentProps {
  pricingOptions: PricingOption[];
  nextCohortDate: string;
}

export default function PricingEnrollment({ pricingOptions, nextCohortDate }: PricingEnrollmentProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleEnroll = () => {
    if (!isHydrated) return;
    alert('Enrollment form would open here. This is a demo.');
  };

  return (
    <section id="enrollment" className="py-16 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground mb-4">
            Choose Your Learning Path
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            Flexible payment options designed to fit your budget and learning goals.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {pricingOptions.map((option, index) => (
            <div
              key={index}
              className={`relative bg-card rounded-2xl border-2 p-8 transition-all duration-300 ${
                option.popular
                  ? 'border-primary shadow-sky-lg scale-105'
                  : 'border-border hover:border-primary hover:shadow-sky-md'
              }`}
            >
              {option.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1 bg-gradient-to-r from-primary to-accent text-primary-foreground rounded-full text-sm font-semibold shadow-sky-sm">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                  {option.name}
                </h3>
                <div className="flex items-baseline justify-center space-x-2">
                  <span className="text-4xl font-headline font-bold text-primary">
                    {option.price}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {option.duration}
                  </span>
                </div>
              </div>

              <ul className="space-y-4 mb-8">
                {option.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start space-x-3">
                    <Icon
                      name="CheckCircleIcon"
                      size={20}
                      variant="solid"
                      className="text-success flex-shrink-0 mt-0.5"
                    />
                    <span className="text-sm text-foreground font-body">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => {
                  if (!isHydrated) return;
                  setSelectedPlan(index);
                  handleEnroll();
                }}
                className={`w-full py-3 rounded-lg font-headline font-semibold transition-all duration-300 ${
                  option.popular
                    ? 'bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-sky-sm hover:shadow-sky-md hover:scale-105'
                    : 'bg-surface border-2 border-primary text-primary hover:bg-primary/5'
                }`}
              >
                Enquire
              </button>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl p-8 text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="CalendarIcon" size={24} variant="solid" className="text-primary" />
            <h3 className="text-xl font-headline font-bold text-foreground">
              Next Cohort Starts
            </h3>
          </div>
          <p className="text-3xl font-headline font-bold text-primary mb-2">
            {nextCohortDate}
          </p>
          <p className="text-sm text-muted-foreground font-body">
            Limited seats available. Secure your spot today!
          </p>
        </div>
      </div>
    </section>
  );
}